import Dialog from "@mui/material/Dialog";
import Divider from "@mui/material/Divider";
import { SelectChangeEvent } from "@mui/material/Select";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useSnackbar } from "../../../components/Snackbar";
import {
  buildNewWorkspace,
  createWorkspace,
  updateWorkspace,
} from "../../../apis/bacchus/workspace";
import { Workspace, WorkspaceVisibility } from "../../../types/workspace";
import { areStringsEqual, getWorkspaceNames, isNameDuplicate } from "../utils";
import { WorkspaceDialogActions } from "./WorkspaceDialogActions";
import { WorkspaceDialogContent } from "./WorkspaceDialogContent";
import { WorkspaceDialogTitle } from "./WorkspaceDialogTitle";
import { getLoggedUserProfile } from "../../../utils/auth";
import { useFeatureFlag } from "../../../hooks/useLaunchDarkly";
import { useTaxonomyContext } from "../../../contexts/Taxonomy/TaxonomyContext";
import { GraphQLClient } from "../../../utils/apollo";
import {
  isResultEmpty,
  resolveTenantPreferences,
  ResolverResult,
} from "../../../utils/workspace/resolver";

const ORG_DEFAULTS_FLAG = "maProjectSegmentImprovements";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  workspaces: Workspace[];
  loadMyWorkspaces: () => Promise<void>;
  workspaceToEdit?: Workspace;
  defaultWorkspaceName?: string;
  isPublicView?: boolean;
}

/**
 * WorkspaceDialog is a component that allows users to create or edit a workspace.
 */
export function WorkspaceDialog(props: Props) {
  const userProfile = getLoggedUserProfile();
  const userName = userProfile?.email?.split("@")[0];
  const { showSuccess, showError } = useSnackbar();
  const orgDefaultsEnabled = useFeatureFlag(ORG_DEFAULTS_FLAG, false);
  const taxonomy = useTaxonomyContext();

  const isEditMode = Boolean(props.workspaceToEdit);

  // Form state
  const [workspaceName, setWorkspaceName] = useState(
    props.defaultWorkspaceName ?? ""
  );
  const [workspaceVisibility, setWorkspaceVisibility] =
    useState<WorkspaceVisibility>(WorkspaceVisibility.PRIVATE);
  const [workspaceDescription, setWorkspaceDescription] = useState("");
  const [useOrgDefaults, setUseOrgDefaults] = useState(false);

  type PreviewState =
    | { status: "idle" }
    | { status: "loading" }
    | { status: "ready"; result: ResolverResult; isEmpty: boolean }
    | { status: "error" };
  const [preview, setPreview] = useState<PreviewState>({ status: "idle" });

  // Validation state
  const [isNameInError, setIsNameInError] = useState(false);
  const [nameErrorMessage, setNameErrorMessage] = useState("");
  const [isDescriptionInError, setIsDescriptionInError] = useState(false);
  const [descriptionErrorMessage, setDescriptionErrorMessage] = useState("");

  const [isProcessingSave, setIsProcessingSave] = useState(false);

  const isFormInvalid = useMemo(() => {
    return (
      isNameInError ||
      !workspaceDescription ||
      workspaceDescription.trim().length === 0
    );
  }, [isNameInError, workspaceDescription]);

  const hasFormBeenModified = useMemo(() => {
    const hasNameChanged = !areStringsEqual(
      workspaceName,
      props.workspaceToEdit?.name
    );
    const hasDescriptionChanged = !areStringsEqual(
      workspaceDescription,
      props.workspaceToEdit?.description
    );
    const hasVisibilityChanged =
      workspaceVisibility !== props.workspaceToEdit?.visibility;

    return hasNameChanged || hasDescriptionChanged || hasVisibilityChanged;
  }, [
    workspaceName,
    workspaceDescription,
    workspaceVisibility,
    props.workspaceToEdit,
  ]);

  const isPreviewLoading = useOrgDefaults && preview.status === "loading";

  const isApplyButtonDisabled = useMemo(() => {
    if (isEditMode) {
      return isProcessingSave || isFormInvalid || !hasFormBeenModified;
    }

    return isProcessingSave || isFormInvalid || isPreviewLoading;
  }, [
    isProcessingSave,
    isFormInvalid,
    hasFormBeenModified,
    isEditMode,
    isPreviewLoading,
  ]);

  // Initialize form fields
  useEffect(() => {
    if (props.isOpen) {
      if (isEditMode && props.workspaceToEdit) {
        setWorkspaceName(props.workspaceToEdit.name);
        setWorkspaceVisibility(props.workspaceToEdit.visibility);
        setWorkspaceDescription(props.workspaceToEdit.description);
      } else {
        setWorkspaceVisibility(
          props.isPublicView
            ? WorkspaceVisibility.PUBLIC
            : WorkspaceVisibility.PRIVATE
        );
      }
    }
  }, [
    props.workspaces,
    props.isOpen,
    props.workspaceToEdit,
    userName,
    isEditMode,
    props.isPublicView,
  ]);

  // Validate name
  useEffect(() => {
    if (props.isOpen) {
      if (workspaceName.trim().length < 3) {
        setIsNameInError(true);
        setNameErrorMessage("Minimum 3 characters");
        return;
      }

      const existingWorkspaceNames = getWorkspaceNames(
        isEditMode
          ? props.workspaces.filter(
              (workspace) => workspace.id !== props.workspaceToEdit?.id
            )
          : props.workspaces
      );

      if (isNameDuplicate(workspaceName, existingWorkspaceNames)) {
        setIsNameInError(true);
        setNameErrorMessage(
          "This project name already exists, please enter a new one"
        );
        return;
      }

      setIsNameInError(false);
      setNameErrorMessage("");
    }
  }, [
    props.workspaces,
    workspaceName,
    isEditMode,
    props.workspaceToEdit,
    props.isOpen,
  ]);

  function handleNameChange(value: ChangeEvent<HTMLInputElement>) {
    const newName = value.target.value;
    setWorkspaceName(newName);
  }

  function handleVisibilityChange(event: SelectChangeEvent) {
    setWorkspaceVisibility(event.target.value as WorkspaceVisibility);
  }

  function handleDescriptionChange(value: ChangeEvent<HTMLInputElement>) {
    const newDescription = value.target.value;
    setWorkspaceDescription(newDescription);

    if (isDescriptionInError && newDescription && newDescription.trim()) {
      setIsDescriptionInError(false);
      setDescriptionErrorMessage("");
    }
  }

  function resetForm() {
    setWorkspaceName(props.defaultWorkspaceName ?? "");
    setWorkspaceVisibility(WorkspaceVisibility.PRIVATE);
    setIsDescriptionInError(false);
    setDescriptionErrorMessage("");
    setWorkspaceDescription("");
    setUseOrgDefaults(false);
    setPreview({ status: "idle" });
  }

  async function loadPreview() {
    setPreview({ status: "loading" });
    try {
      const apolloClient = GraphQLClient();
      const result = await resolveTenantPreferences(apolloClient, {
        hgIndustries: taxonomy.hgIndustries ?? [],
        countries: taxonomy.countries ?? [],
      });
      const empty = isResultEmpty(result);
      setPreview({ status: "ready", result, isEmpty: empty });
      if (empty) setUseOrgDefaults(false);
    } catch (err) {
      console.error("Failed to load organization preferences preview", err);
      setPreview({ status: "error" });
      setUseOrgDefaults(false);
    }
  }

  function handleUseOrgDefaultsChange(event: ChangeEvent<HTMLInputElement>) {
    const { checked } = event.target;
    setUseOrgDefaults(checked);
    if (checked && preview.status === "idle") {
      void loadPreview();
    }
    if (checked && preview.status === "error") {
      void loadPreview();
    }
  }

  function redirectToNewWorkspace(workspaceId: string) {
    showSuccess(
      "Project created! You'll be redirected to the market configuration page.."
    );
    setTimeout(() => {
      const path = `/projects/${workspaceId}/configuration?source=newWorkspace`;
      // Using window.location.href instead of navigate to force state refresh
      window.location.href = window.location.origin + path;
    }, 1000);
  }

  async function handleSaveNewWorkspace() {
    setIsProcessingSave(true);

    try {
      const builtWorkspace = buildNewWorkspace(
        workspaceName,
        workspaceVisibility,
        workspaceDescription
      );

      if (useOrgDefaults && builtWorkspace.configuration) {
        // Preview is already loaded by the checkbox handler. If we don't
        // have a ready result here, the user clicked Create faster than the
        // fetch finished — fall back to resolving inline.
        const result =
          preview.status === "ready"
            ? preview.result
            : await resolveTenantPreferences(GraphQLClient(), {
                hgIndustries: taxonomy.hgIndustries ?? [],
                countries: taxonomy.countries ?? [],
              });

        builtWorkspace.configuration = {
          ...builtWorkspace.configuration,
          sam: result.configuration.sam,
          som: result.configuration.som,
          competitorsProductIds: result.configuration.competitorsProductIds,
        };
      }

      const res = await createWorkspace(builtWorkspace);
      redirectToNewWorkspace(res.data.data.id);
    } catch (error) {
      console.error("Failed to create new workspace " + error);
      showError(
        useOrgDefaults
          ? "We couldn't apply your Organization Preferences. Please try again or uncheck the option."
          : "We encountered an error while creating the project, please try again"
      );
      setIsProcessingSave(false);
    }
  }

  function handleDescriptionBlur() {
    if (!workspaceDescription) {
      setIsDescriptionInError(true);
      setDescriptionErrorMessage("Please enter a description");
    }
  }

  async function handleEditWorkspace() {
    if (props.workspaceToEdit) {
      setIsProcessingSave(true);
      const res = await updateWorkspace({
        id: props.workspaceToEdit.id,
        name: workspaceName,
        description: workspaceDescription,
        visibility: workspaceVisibility,
      });

      if (res.isSuccessful) {
        showSuccess("Successfully updated workspace");
        props.loadMyWorkspaces();
      } else {
        showError(
          "We encountered an error while updating the workspace, please try again"
        );
      }

      props.onClose();
      setIsProcessingSave(false);
    }
  }

  const baseId = isEditMode ? "edit-workspace" : "create-new-workspace";

  return (
    <Dialog
      id={`${baseId}-dialog`}
      open={props.isOpen}
      onClose={props.onClose}
      aria-labelledby="workspace-dialog-title"
      maxWidth="md"
      fullWidth
      slotProps={{
        transition: {
          onExited: resetForm,
        },
      }}
    >
      <WorkspaceDialogTitle
        baseId={baseId}
        isEditMode={isEditMode}
        onClose={props.onClose}
      />
      <Divider />
      <WorkspaceDialogContent
        baseId={baseId}
        workspaceName={workspaceName}
        workspaceVisibility={workspaceVisibility}
        workspaceDescription={workspaceDescription}
        isNameInError={isNameInError}
        nameErrorMessage={nameErrorMessage}
        isDescriptionInError={isDescriptionInError}
        descriptionErrorMessage={descriptionErrorMessage}
        handleNameChange={handleNameChange}
        handleVisibilityChange={handleVisibilityChange}
        handleDescriptionChange={handleDescriptionChange}
        handleDescriptionBlur={handleDescriptionBlur}
        isOpen={props.isOpen}
        showUseOrgDefaults={orgDefaultsEnabled && !isEditMode}
        useOrgDefaults={useOrgDefaults}
        handleUseOrgDefaultsChange={handleUseOrgDefaultsChange}
        orgDefaultsPreview={preview}
      />
      <WorkspaceDialogActions
        baseId={baseId}
        isEditMode={isEditMode}
        onClose={props.onClose}
        handleEditWorkspace={handleEditWorkspace}
        handleSaveNewWorkspace={handleSaveNewWorkspace}
        isApplyButtonDisabled={isApplyButtonDisabled}
        isProcessing={isProcessingSave}
      />
    </Dialog>
  );
}
